import type { ButtonProps } from '@fz/ui-components-react';
import { getResource } from '@utils/resource-translator.util';

export const WEB_SESSION_CMS_KEY = {
  MULTI_SESSION_FOUND: 'MULTISESSIONFOUND',
  SESSION_EXPIRED: 'MULTISESSIONCLEARD',
} as const;

export interface WebSessionActionPopupHandlers {
  onClose: () => void;
  onContinue: () => void;
  onCancel: () => void;
}

export type PopupAction = ButtonProps;

export interface WebSessionActionPopupViewProps extends WebSessionActionPopupHandlers {
  title: string;
  message: string;
  popupClassName: string;
  showHeaderBackground: boolean;
  showCloseButton: boolean;
  sessionClassName: {
    header: string;
    mainContent: string;
    content: string;
    action: string;
  };
  actions: PopupAction[];
  layout: 'horizontal';
  align: 'left' | 'center' | 'right';
  actionsAlign: 'left' | 'center' | 'right';
  groupButtons: false;
  size: 'custom';
  titleClass: {
    class: 'label1';
    type: 'medium';
  };
}

const WEB_SESSION_ACTION_POPUP_COPY = {
  [WEB_SESSION_CMS_KEY.MULTI_SESSION_FOUND]: {
    title: 'SessionTitle.Text',
    message: 'SessionMessage.Text',
    continueLabel: 'NewSession.Text',
    cancelLabel: 'Cancel.Text',
  },
  [WEB_SESSION_CMS_KEY.SESSION_EXPIRED]: {
    title: 'ExpiryTitle.Text',
    message: 'ExpiryTitle.Text',
    okLabel: 'GotIt.Text',
  },
  FALLBACK: {
    title: 'General error',
    okLabel: 'GotIt.Text',
  },
} as const;

const WEB_SESSION_ACTION_POPUP_CONFIG = {
  popupClassName: 'action-popup-layout',
  showHeaderBackground: true,
  showCloseButton: false,
  sessionClassName: {
    header: 'websessionpopup-header',
    mainContent: 'websessionpopup-wrapper',
    content: 'websessionpopup-content',
    action: 'websessionpopup-action',
  },
  continueAction: {
    variant: 'solid',
    variantColor: 'primary',
    className: 'websession',
    size: 'lg',
  },
  cancelAction: {
    variant: 'outline',
    variantColor: 'primary',
    className: 'websession',
    size: 'lg',
  },
  layout: 'horizontal',
  align: 'left',
  actionsAlign: 'right',
  groupButtons: false,
  size: 'custom',
  titleClass: {
    class: 'label1',
    type: 'medium',
  },
} as const;

const resolveTitle = (cmsKey: string | null): string => {
  if (cmsKey === WEB_SESSION_CMS_KEY.MULTI_SESSION_FOUND) {
    return WEB_SESSION_ACTION_POPUP_COPY[WEB_SESSION_CMS_KEY.MULTI_SESSION_FOUND].title;
  }
  if (cmsKey === WEB_SESSION_CMS_KEY.SESSION_EXPIRED) {
    return WEB_SESSION_ACTION_POPUP_COPY[WEB_SESSION_CMS_KEY.SESSION_EXPIRED].title;
  }
  return cmsKey ?? WEB_SESSION_ACTION_POPUP_COPY.FALLBACK.title;
};

const resolveMessage = (cmsKey: string | null): string => {
  if (cmsKey === WEB_SESSION_CMS_KEY.MULTI_SESSION_FOUND) {
    return getResource(
      WEB_SESSION_ACTION_POPUP_COPY[WEB_SESSION_CMS_KEY.MULTI_SESSION_FOUND].message,
    );
  }
  if (cmsKey === WEB_SESSION_CMS_KEY.SESSION_EXPIRED) {
    return getResource(WEB_SESSION_ACTION_POPUP_COPY[WEB_SESSION_CMS_KEY.SESSION_EXPIRED].message);
  }
  return '';
};

const buildActions = (
  cmsKey: string | null,
  handlers: WebSessionActionPopupHandlers,
): PopupAction[] => {
  const { continueAction, cancelAction } = WEB_SESSION_ACTION_POPUP_CONFIG;

  if (cmsKey === WEB_SESSION_CMS_KEY.SESSION_EXPIRED) {
    return [
      {
        children: getResource(
          WEB_SESSION_ACTION_POPUP_COPY[WEB_SESSION_CMS_KEY.SESSION_EXPIRED].okLabel,
        ),
        ...continueAction,
        onClick: handlers.onClose,
      },
    ];
  }

  if (cmsKey === WEB_SESSION_CMS_KEY.MULTI_SESSION_FOUND) {
    return [
      {
        children: getResource(
          WEB_SESSION_ACTION_POPUP_COPY[WEB_SESSION_CMS_KEY.MULTI_SESSION_FOUND].continueLabel,
        ),
        ...continueAction,
        onClick: handlers.onContinue,
      },
      {
        children: getResource(
          WEB_SESSION_ACTION_POPUP_COPY[WEB_SESSION_CMS_KEY.MULTI_SESSION_FOUND].cancelLabel,
        ),
        ...cancelAction,
        onClick: handlers.onCancel,
      },
    ];
  }

  return [
    {
      children: getResource(WEB_SESSION_ACTION_POPUP_COPY.FALLBACK.okLabel),
      ...continueAction,
      onClick: handlers.onCancel,
    },
  ];
};

export const mapWebSessionActionPopupViewProps = (
  cmsKey: string | null,
  handlers: WebSessionActionPopupHandlers,
): WebSessionActionPopupViewProps => ({
  title: getResource(resolveTitle(cmsKey)),
  message: resolveMessage(cmsKey),
  popupClassName: WEB_SESSION_ACTION_POPUP_CONFIG.popupClassName,
  showHeaderBackground: WEB_SESSION_ACTION_POPUP_CONFIG.showHeaderBackground,
  showCloseButton: WEB_SESSION_ACTION_POPUP_CONFIG.showCloseButton,
  sessionClassName: WEB_SESSION_ACTION_POPUP_CONFIG.sessionClassName,
  actions: buildActions(cmsKey, handlers),
  layout: WEB_SESSION_ACTION_POPUP_CONFIG.layout,
  align: WEB_SESSION_ACTION_POPUP_CONFIG.align,
  actionsAlign:
    cmsKey === WEB_SESSION_CMS_KEY.MULTI_SESSION_FOUND
      ? WEB_SESSION_ACTION_POPUP_CONFIG.actionsAlign
      : 'center',
  groupButtons: WEB_SESSION_ACTION_POPUP_CONFIG.groupButtons,
  size: WEB_SESSION_ACTION_POPUP_CONFIG.size,
  titleClass: WEB_SESSION_ACTION_POPUP_CONFIG.titleClass,
  onClose: handlers.onClose,
  onContinue: handlers.onContinue,
  onCancel: handlers.onCancel,
});
